import * as Vue from 'vue';

import * as Sentry from '@sentry/vue';

import DonutChart from 'vue-css-donut-chart';
import VTooltip from 'floating-vue';
import { uniq } from 'lodash';

import { I18n } from 'i18n-js';

// Be advised that the emojiVersion used in init must always be <= the version that Slack is currently using.
// As of this comment (5/7/25), the version of emoji used by Slack is 14
// KEEP THIS IN SYNC WITH THE `emojiVersion` ARG LATER IN THIS FILE!
import emojiData from '@emoji-mart/data/sets/14/apple.json';
import { init as initEmoji } from 'emoji-mart';

import { store } from '../store/index';
import Network from '../lib/api/network';

import 'floating-vue/dist/style.css';

import translations from '../locales/en.json';

// These are feature flags for Vue3 to remove/add some enforcement of various rules. The documentation for these online is pretty good if you are curious.
Vue.configureCompat({
  RENDER_FUNCTION: false, // This are required for getting 'floating-vue's VTooltip directive to work with the Vue3 migration build (@vue/compat)
  WATCH_ARRAY: false, // This are required for getting 'floating-vue's VTooltip directive to work with the Vue3 migration build (@vue/compat)
  COMPONENT_V_MODEL: false, // This is because 'modelValue' is correct syntax for Vue3, but the @vue/compat build doesn't like it
  COMPONENT_ASYNC: false, // Disable the async component warning, necessary for Lucide icon components
  ATTR_FALSE_VALUE: false, // We want to render false values for props instead of removing their keys, so no need for this warning.
});


const plugins = require.context('../plugins', true, /\.\/[^/]+\.(js|coffee)$/);
const directives = require.context('../directives', true, /\.\/[^/]+\.(js|coffee)$/);
// Require all files ending with .coffee, .vue, .js EXCEPT for files ending with .stories.js
// .stories.js are processed separately by Storybook's own webpack build
const rawComponents = require.context('../components', true, /(?<!\.stories)\.(coffee|vue|js)$/);
const componentLookup = {};
rawComponents.keys().forEach((key) => {
  const component = rawComponents(key).default;
  const name = key.split('.')[1].split('/').slice(1).join('-').replace(/_/g, '-');
  componentLookup[name] = component;
});


// Initialize

$(() => {
  const vueElements = $('#vue, .vue');
  const vueApps = [];
  if (vueElements.length > 0) {
    // Initialize base state from Donut.Args
    store.commit('updateState', Donut.Args);

    // Keep the session-expiry warning modal's countdown in sync with the server's view of
    // last_request_at after every real request (see store/session-timeout/actions.js).
    Network.setSessionTimeoutHeadersHandler((timeout) => store.dispatch('updateSessionTimeout', timeout));
    // expiresAt/warnAt have exactly one source: the headers above. A page can't read the response
    // headers of its own navigation request (no DOM API exposes them), so this one
    // GET is what seeds them for the very first page load.
    Network.get('/session_keep_alive', {});

    // Initialize module-specific state from Donut.ModuleArgs
    for (const module in Donut.ModuleArgs) {
      const moduleArgs = Donut.ModuleArgs[module];
      for (const key in moduleArgs) {
        const value = moduleArgs[key];
        store.commit('update', { module, key, value });
      }
    }

    vueElements.each((vueElIdx, element) => {
      const component = componentLookup[element.id];
      if (component === undefined) {
        const sentryMessage = `VueComponentMissingFromComponentLookup: ${element.tagName}`;
        console.log(sentryMessage);

        if (window.SENTRY_FRONTEND_DSN) {
          window.Sentry.captureMessage(sentryMessage);
        }
        return;
      }

      // Set initial vue props to the value of `v-bind` or an empty array
      const props = JSON.parse(element.getAttribute('v-bind')) || {};
      // Add any `:` prefixed attributes on the root vue element to props
      if (element.hasAttributes()) {
        for (const a of element.attributes) {
          if (a.name.charAt(0) === ':') {
            props[a.name.slice(1)] = JSON.parse(a.value);
          }
        }
      }

      const app = Vue.createApp(componentLookup[element.id], props);
      app.use(VTooltip, {
        themes: {
          tooltip: {
            delay: {
              show: 0,
              hide: 0,
            },
          },
        },
      });
      app.use(DonutChart);
      app.use(store);

      const i18n = new I18n(translations);
      i18n.missingBehavior = 'error';
      app.provide('$i18n', i18n);

      window.SENTRY_FRONTEND_DSN = process.env.SENTRY_FRONTEND_DSN;
      if (window.SENTRY_FRONTEND_DSN) {
        Sentry.init({
          app,
          environment: process.env.NODE_ENV,
          dsn: window.SENTRY_FRONTEND_DSN,
          integrations: [],
          tracesSampleRate: 0.1,

          // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
          tracePropagationTargets: ['localhost', /^https:\/\/app\.donut\.ai/],
        });
        window.Sentry = Sentry;
      }

      plugins.keys().forEach((key) => {
        const plugin = plugins(key).default;
        app.use(plugin);
      });
      directives.keys().forEach((key) => {
        const directive = directives(key).default;
        const name = key.split('.')[1].split('/').slice(1).join('-').replace(/_/g, '-');
        app.directive(name, directive);
      });
      Object.keys(componentLookup).forEach((name) => {
        const componentForName = componentLookup[name];
        app.component(name, componentForName);
      });

      // Sentry should only be initialized the once, but before the apps have been mounted.
      // Thus, we do it after this loop once the apps are ready to be mounted, initializing
      // it with all of them as an array, and _then_ mounting them.
      vueApps.push({ app, element });
    });

    // Initialize Sentry
    window.SENTRY_FRONTEND_DSN = process.env.SENTRY_FRONTEND_DSN;
    if (window.SENTRY_FRONTEND_DSN) {
      Sentry.init({
        app: vueApps.map(a => a.app),
        environment: process.env.NODE_ENV,
        dsn: window.SENTRY_FRONTEND_DSN,
        integrations: [],
        tracesSampleRate: 0.1,
        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['localhost', /^https:\/\/app\.donut\.ai/],
      });
      const { currentUser, currentTeam } = Donut.Args;
      // If we have access to these Donut.Args values, setUser here
      if (currentUser && currentTeam) {
        Sentry.setUser({
          email: currentUser.email,
          id: currentUser.id,
          uid: currentUser.uid,
          teamId: currentTeam.id,
          teamUid: currentTeam.uid,
        });
      }
      window.Sentry = Sentry;
    }

    // Initialize Emoji Mart, including custom emojis if present.
    if (Donut.Args.customEmojis) {
      // Register custom aliases.
      Object.assign(emojiData.aliases, Donut.Args.customEmojis.aliases);
      // Default emojis with custom aliases have extra searchable keywords.
      Donut.Args.customEmojis.default.forEach((emoji) => {
        if (!emojiData.emojis[emoji.id]) return; // Shouldn't be possible, but just in case!

        emojiData.emojis[emoji.id].keywords = uniq(emojiData.emojis[emoji.id].keywords.concat(emoji.keywords));
      });
    }
    const customEmojis = Donut.Args.customEmojis?.custom || [];
    // Be advised that the emojiVersion used in init must always be <= the version that Slack is currently using.
    // As of this comment (5/7/25), the version of emoji used by Slack is 14
    initEmoji({
      data: emojiData,
      custom: [{ id: 'customSlack', name: 'Custom', emojis: customEmojis }],
      emojiVersion: '14', // KEEP THIS IN SYNC WITH THE `emojiData` IMPORT AT THE TOP OF THIS FILE!
      set: 'apple',
      categoryIcons: { customSlack: { svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="2.88 2.88 19.296 19.248"><path d="M13 10C13 11.1046 13.8954 12 15 12C16.1046 12 17 11.1046 17 10V5C17 3.89543 16.1046 3 15 3C13.8954 3 13 3.89543 13 5V10ZM5 8C3.89543 8 3 8.89543 3 10C3 11.1046 3.89543 12 5 12H10C11.1046 12 12 11.1046 12 10C12 8.89543 11.1046 8 10 8H5ZM15 13C13.8954 13 13 13.8954 13 15C13 16.1046 13.8954 17 15 17H20C21.1046 17 22 16.1046 22 15C22 13.8954 21.1046 13 20 13H15ZM10 22C8.89543 22 8 21.1046 8 20L8 15C8 13.8954 8.89543 13 10 13C11.1046 13 12 13.8954 12 15V20C12 21.1046 11.1046 22 10 22ZM8 5C8 3.89543 8.89543 3 10 3C11.1046 3 12 3.89543 12 5V7H10C8.89543 7 8 6.10457 8 5ZM3 15C3 16.1046 3.89543 17 5 17C6.10457 17 7 16.1046 7 15V13H5C3.89543 13 3 13.8954 3 15ZM17 20C17 21.1046 16.1046 22 15 22C13.8954 22 13 21.1046 13 20V18H15C16.1046 18 17 18.8954 17 20ZM22 10C22 8.89543 21.1046 8 20 8C18.8954 8 18 8.89543 18 10V12H20C21.1046 12 22 11.1046 22 10Z" /></svg>' } },
    });
    // Create array of all emoji names that should be recognized for autocomplete.
    // 1. Default emoji names
    const autocompleteEmojiNames = Object.values(emojiData.emojis).map(e => e.id);
    // 2. Custom emoji names
    autocompleteEmojiNames.push(...customEmojis.map(e => e.id));
    // 3. Aliases for both default and custom emojis
    autocompleteEmojiNames.push(...Object.keys(emojiData.aliases));
    // Put the full list of emoji into the window so that the autocomplete strategy can access it
    Donut.Emoji.emojiSet = uniq(autocompleteEmojiNames);

    vueApps.forEach(({ app, element }) => {
      app.mount(`#${element.id}`);
    });
  }
});
